import React, { ReactElement, useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { ComboBoxOption } from './ComboBoxOption';
import generateDynamicRegExp, { ComboBoxFilterOptions } from './generateDynamicRegularExpression';

type ComboBoxState = {
  showList: boolean;
  value: string | number | undefined;
  focusedIndex: number | null;
  noSelectionIndex: number;
  displayValue: string;
  pristine: boolean;
  filteredOptions: ComboBoxOption[];
};

type ComboBoxHandlers = {
  clearInput: () => void;
  toggleList: () => void;
  handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleInputClick: () => void;
  handleInputKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  handleOptionClick: (option: ComboBoxOption) => void;
  handleOptionKeyDown: (event: React.KeyboardEvent<HTMLLIElement>, option: ComboBoxOption) => void;
  handleOptionMouseMove: (index: number) => void;
};

type ComboBoxRefs = {
  comboBoxRef: React.MutableRefObject<HTMLDivElement | null>;
  selectRef: React.MutableRefObject<HTMLSelectElement | null>;
  inputRef: React.MutableRefObject<HTMLInputElement | null>;
  listRef: React.MutableRefObject<HTMLUListElement | null>;
  focusRef: React.MutableRefObject<HTMLLIElement | null>;
};

const DEFAULT_FILTER = '.*{{query}}.*';

const useComboBox = (
  ref: React.MutableRefObject<HTMLSelectElement | null> | ((instance: HTMLSelectElement | null) => void) | null,
  children: ReactElement<HTMLOptionElement> | ReactElement<HTMLOptionElement>[],
  defaultValue?: string | number,
  controlledValue?: string | number,
  filter: string = DEFAULT_FILTER,
  filterOptions?: ComboBoxFilterOptions,
  disableFiltering = false,
): ComboBoxState & ComboBoxHandlers & ComboBoxRefs => {
  const options: ComboBoxOption[] = useMemo(
    () =>
      (Array.isArray(children) ? children : [children]).map((option) => ({
        value: option.props.value,
        text: React.Children.map(option.props.children, (child) => child).join(''),
      })),
    [children],
  );

  const [showList, setShowList] = useState(false);
  const [value, setValue] = useState(() => (defaultValue !== undefined ? defaultValue : controlledValue));
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const [shouldScroll, setShouldScroll] = useState(false);
  const [displayValue, setDisplayValue] = useState(
    () => options.find((option) => option.value && option.value === value)?.text || '',
  );
  const [pristine, setPristine] = useState(() => Boolean(value));
  const lowerCaseInputValue = (displayValue || '').toLowerCase();
  const regex = generateDynamicRegExp(filter, lowerCaseInputValue, filterOptions);
  const filteredOptions = options.filter(
    (option) => option.value && (disableFiltering || pristine || !lowerCaseInputValue || regex.test(option.text)),
  );
  const noSelectionIndex = Math.max(
    0,
    filteredOptions.findIndex((option) => {
      if (disableFiltering && regex.test(option.text)) {
        return true;
      }

      return false;
    }),
  );
  const shouldFocusOption = focusedIndex !== null && focusedIndex > -1;
  const shouldFocusInput = focusedIndex !== null && focusedIndex < 0;
  const comboBoxRef = useRef<HTMLDivElement | null>(null);
  const selectRef = useRef<HTMLSelectElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const focusRef = useRef<HTMLLIElement | null>(null);

  // Sync local selectRef and forwarded ref
  useEffect(() => {
    if (!ref) {
      return;
    }
    if (typeof ref === 'function') {
      ref(selectRef.current);
    } else {
      // eslint-disable-next-line no-param-reassign
      ref.current = selectRef.current;
    }
  }, [ref]);

  // Scroll effect when using arrow keys
  useEffect(() => {
    if (focusRef.current && shouldFocusOption) {
      focusRef.current.focus({ preventScroll: true });
    }
    if (
      focusRef.current &&
      listRef.current &&
      (shouldFocusOption || shouldScroll || (disableFiltering && displayValue))
    ) {
      const optionBottom = focusRef.current.offsetTop + focusRef.current.offsetHeight;
      const currentBottom = listRef.current.scrollTop + listRef.current.offsetHeight;
      if (optionBottom > currentBottom) {
        listRef.current.scrollTop = optionBottom - listRef.current.offsetHeight;
      }

      if (focusRef.current.offsetTop < listRef.current.scrollTop) {
        listRef.current.scrollTop = focusRef.current.offsetTop;
      }
    }
  }, [shouldFocusOption, shouldScroll, focusedIndex, showList, disableFiltering, displayValue]);

  useEffect(() => {
    if (inputRef.current && shouldFocusInput) {
      inputRef.current.focus({ preventScroll: true });
    }
    if (!showList && listRef.current) {
      listRef.current.scrollTop = 0;
    }
    if (listRef.current && disableFiltering && !displayValue) {
      listRef.current.scrollTop = 0;
    }
  }, [shouldFocusInput, displayValue, showList, disableFiltering]);

  useEffect(() => {
    if (controlledValue !== undefined) {
      setValue(controlledValue);
      const matchingOption = options.find((option) => option.value && option.value === controlledValue);
      setDisplayValue(matchingOption?.text || '');
      setPristine(Boolean(matchingOption?.value));
    }
  }, [controlledValue, options]);

  useEffect(() => {
    if (selectRef.current) {
      const event = new CustomEvent('change', {
        bubbles: true,
        cancelable: true,
        detail: {
          value,
        },
      });
      selectRef.current.dispatchEvent(event);
    }
  }, [value]);

  const hideList = useCallback((): void => {
    setShowList(false);
  }, []);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setDisplayValue(event.target.value);
    setShowList(true);
    setPristine(false);
  };

  const handleInputClick = (): void => {
    setShowList(true);
    setFocusedIndex(-1);
  };

  const toggleList = (): void => {
    setFocusedIndex(-1);
    setShowList(!showList);
    setShouldScroll(true);
  };

  const handleOptionClick = (option: ComboBoxOption): void => {
    setValue(option.value);
    setDisplayValue(option.text);
    hideList();
    setPristine(true);
    setFocusedIndex(-1);
    // Manually focus to not trigger focusout event
    inputRef.current?.focus();
  };

  const resetSelection = useCallback((): void => {
    if (value) {
      const matchedOption = options.find((option) => option.value === value);
      const matchedOptionText = matchedOption ? matchedOption.text : undefined;
      setDisplayValue(matchedOptionText || '');
      setPristine(true);
    } else {
      setDisplayValue('');
    }
  }, [options, value]);

  useEffect(() => {
    /**
     * Instead of using `focusout` event with react, we are using dom event listeners.
     * See https://github.com/facebook/react/issues/6410
     *
     * To properly support IE 11 we need to use 'focusout' instead of onBlur.
     * See https://stackoverflow.com/questions/37609049/how-to-correctly-catch-change-focusout-event-on-text-input-in-react-js/59875006#59875006
     * for additional info.
     */
    const handleFocusOut = (event: HTMLElementEventMap['focusout']): void => {
      if (comboBoxRef.current && comboBoxRef.current.contains(event.relatedTarget as Node)) {
        return;
      }
      resetSelection();
      hideList();
    };
    const comboBoxElement = comboBoxRef.current;
    comboBoxElement?.addEventListener('focusout', handleFocusOut);

    return () => comboBoxElement?.removeEventListener('focusout', handleFocusOut);
  }, [resetSelection, hideList]);

  const handleInputEnter = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    const matchedOption = filteredOptions.find((option) => option.text.toLowerCase() === lowerCaseInputValue);
    if (matchedOption) {
      const matchedOptionValue = matchedOption.value;
      const matchedOptionText = matchedOption.text;
      setValue(matchedOptionValue);
      setDisplayValue(matchedOptionText);
      setPristine(true);
    } else {
      resetSelection();
    }
    hideList();

    event.preventDefault();
  };

  const handleInputArrowDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (!showList) {
      const selectedIndex = Math.max(
        0,
        filteredOptions.findIndex((option) => regex.test(option.text)),
      );
      setFocusedIndex(selectedIndex);
    } else {
      if (focusedIndex !== null && focusedIndex === -1) {
        const selectedIndex = filteredOptions.findIndex((option) => regex.test(option.text));
        setFocusedIndex(selectedIndex);
      }
      if (focusRef.current) {
        focusRef.current.focus({ preventScroll: true });
      }
    }
    setShouldScroll(true);
    setShowList(true);
    event.preventDefault();
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    switch (event.key) {
      case 'Enter':
        handleInputEnter(event);
        break;
      case 'ArrowDown':
        handleInputArrowDown(event);
        break;
      case 'Escape':
        resetSelection();
        hideList();
        break;
      // no default
    }
  };

  const handleOptionKeyDown = (event: React.KeyboardEvent<HTMLLIElement>, option: ComboBoxOption): void => {
    switch (event.key) {
      case 'Enter':
      case 'Tab':
        if (event.shiftKey) {
          return;
        }
        event.preventDefault();
        setValue(option.value);
        setDisplayValue(option.text);
        hideList();
        setPristine(true);
        setFocusedIndex(-1);
        // Manually focus to not trigger focusout event
        inputRef.current?.focus();
        break;
      case 'ArrowDown':
        event.preventDefault();
        // Check if we can't arrow down any more.
        if (focusedIndex && focusedIndex + 1 === filteredOptions.length) {
          return;
        }
        setFocusedIndex(focusedIndex !== null ? focusedIndex + 1 : 0);
        setShouldScroll(true);
        break;
      case 'ArrowUp':
        event.preventDefault();
        // When arrow up from first option, hide the list.
        if (focusedIndex === 0) {
          setShowList(false);
        }
        setFocusedIndex(focusedIndex !== null ? focusedIndex - 1 : 0);
        setShouldScroll(true);
        break;
      case 'Escape':
        hideList();
        resetSelection();
        setFocusedIndex(-1);
        break;
      // no default
    }
  };

  const clearInput = (): void => {
    setValue('');
    setDisplayValue('');
    setPristine(false);
    setFocusedIndex(-1);
  };

  const handleOptionMouseMove = (index: number): void => {
    if (focusedIndex === index) {
      return;
    }
    setFocusedIndex(index);
    setShouldScroll(false);
  };

  return {
    showList,
    focusedIndex,
    noSelectionIndex,
    displayValue,
    pristine,
    value,
    filteredOptions,
    clearInput,
    toggleList,
    handleInputChange,
    handleInputClick,
    handleInputKeyDown,
    handleOptionClick,
    handleOptionKeyDown,
    handleOptionMouseMove,
    comboBoxRef,
    selectRef,
    inputRef,
    listRef,
    focusRef,
  };
};

export default useComboBox;
